import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { calculateStartValue, calculateStartValueGymAct } from "../../scoring/utils/calculateStartValue";
import { skillsData, bonusOptions, getSkillByCode } from "../../scoring/data/skillsData";

const ChartBarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V9m5 7V5m5 11v-6" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const PencilIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 013.182 3.182L7.125 19.588l-4.5 1.125 1.125-4.5L16.862 3.487z" />
  </svg>
);

const BookmarkIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-4-7 4V5z" />
  </svg>
);

const FolderOpenIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9h18" />
  </svg>
);

const EVENTS = [
  { id: "FX", name: "Floor Exercise", shortName: "Floor" },
  { id: "PH", name: "Pommel Horse", shortName: "Pommel" },
  { id: "SR", name: "Still Rings", shortName: "Rings" },
  { id: "VT", name: "Vault", shortName: "Vault" },
  { id: "PB", name: "Parallel Bars", shortName: "P-Bars" },
  { id: "HB", name: "High Bar", shortName: "High Bar" }
];

const ELEMENT_GROUPS = ["EG I", "EG II", "EG III", "EG IV", "Junior A"];

const ROUTINES_COLLECTION = "azbga_routines";
const TEAMS_COLLECTION = "azbga_teams";
const TEAMS_DOC_ID = "boys";

function normalizeRoutineSkill(skill, fallbackEvent) {
  if (!skill) return null;

  const canonicalSkill = skill.code ? getSkillByCode(skill.code) : undefined;
  const instanceId = skill.instanceId || `${skill.code || fallbackEvent || "skill"}-${Date.now()}-${Math.random()}`;

  if (canonicalSkill) {
    return {
      ...canonicalSkill,
      ...skill,
      name: skill.name || canonicalSkill.name,
      value: skill.value ?? canonicalSkill.value,
      group: skill.group || canonicalSkill.group,
      apparatus: skill.apparatus || canonicalSkill.apparatus || fallbackEvent,
      active: skill.active !== false,
      instanceId
    };
  }

  return {
    ...skill,
    name: skill.name || "Unknown Skill",
    code: skill.code || `SKILL-${instanceId}`,
    value: skill.value ?? 0,
    group: skill.group || "EG I",
    apparatus: skill.apparatus || fallbackEvent,
    active: skill.active !== false,
    instanceId
  };
}

function normalizeRoutine(routine, fallbackEvent) {
  const routineEvent = routine?.event || fallbackEvent;
  return {
    ...routine,
    skills: (routine?.skills || [])
      .map(skill => normalizeRoutineSkill(skill, routineEvent))
      .filter(Boolean),
    bonuses: Array.isArray(routine?.bonuses) ? routine.bonuses : []
  };
}

const RoutineCalculator = () => {
  const { user: currentUser } = useAuth();
  const [currentEvent, setCurrentEvent] = useState("FX");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [level, setLevel] = useState(8);
  const [track, setTrack] = useState("national");
  const [gymActStickApplied, setGymActStickApplied] = useState(false);
  const [bonuses, setBonuses] = useState(() => getBonusOptions(8));
  const [searchQuery, setSearchQuery] = useState("");
  const [editingSkillIndex, setEditingSkillIndex] = useState(null);
  const [showEditSkillDialog, setShowEditSkillDialog] = useState(false);
  const [selectedEGFilter, setSelectedEGFilter] = useState("All");

  const [savedRoutines, setSavedRoutines] = useState([]);
  const [isLoadingRoutines, setIsLoadingRoutines] = useState(true);
  const [currentRoutineId, setCurrentRoutineId] = useState(null);
  const [routineName, setRoutineName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [selectedAthleteId, setSelectedAthleteId] = useState("");
  const [routineFilterAthleteId, setRoutineFilterAthleteId] = useState("");
  const [isLoadingRoutine, setIsLoadingRoutine] = useState(false);

  const [athletes, setAthletes] = useState([]);

  const [newSkill, setNewSkill] = useState({
    skillName: "",
    value: 0.3,
    group: "EG I"
  });

  function getBonusOptions(currentLevel) {
    return bonusOptions
      .filter(bonus => !(bonus.name === "Stuck Dismount" && currentLevel !== 7))
      .map(bonus => {
        if (bonus.name === "Stuck Dismount") {
          return { ...bonus, value: 0.1 };
        }
        return bonus;
      });
  }

  useEffect(() => {
    setBonuses(prevBonuses =>
      getBonusOptions(level).map((bonus, i) => ({
        ...bonus,
        applied: prevBonuses[i]?.applied ?? false
      }))
    );
  }, [level]);

  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        const teamsCollection = collection(db, TEAMS_COLLECTION);
        const teamsQuery = query(teamsCollection, where("__name__", "==", TEAMS_DOC_ID));
        const teamsSnapshot = await getDocs(teamsQuery);

        let athletesList = [];
        teamsSnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          if (data.members) {
            athletesList = data.members;
          }
        });

        setAthletes(athletesList);
      } catch (error) {
        console.error("Error fetching athletes:", error);
      }
    };

    if (currentUser) {
      fetchAthletes();
    }
  }, [currentUser, currentEvent]);

  useEffect(() => {
    const fetchRoutines = async () => {
      setIsLoadingRoutines(true);
      try {
        const routinesCollection = collection(db, ROUTINES_COLLECTION);
        const routinesQuery = query(
          routinesCollection,
          where("ownerId", "==", currentUser.uid),
          orderBy("updatedAt", "desc")
        );
        const routinesSnapshot = await getDocs(routinesQuery);

        const routinesList = [];
        routinesSnapshot.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          routinesList.push(normalizeRoutine({
            id: docSnapshot.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
          }, currentEvent));
        });

        setSavedRoutines(routinesList);
      } catch (error) {
        console.error("Error fetching routines:", error);
      } finally {
        setIsLoadingRoutines(false);
      }
    };

    if (currentUser) {
      fetchRoutines();
    }
  }, [currentUser, currentEvent]);

  const previousEventRef = useRef(currentEvent);

  useEffect(() => {
    if (isLoadingRoutine) return;
    if (previousEventRef.current !== currentEvent) {
      setSelectedSkills([]);
      setBonuses(getBonusOptions(level).map(bonus => ({ ...bonus, applied: false })));
      setSearchQuery("");
      setShowEditSkillDialog(false);
      setEditingSkillIndex(null);
      setCurrentRoutineId(null);
      setRoutineName("");
      setSelectedAthleteId("");
      previousEventRef.current = currentEvent;
    }
  }, [currentEvent, isLoadingRoutine, level]);

  useEffect(() => {
    if (track === "gymact") {
      setLevel(10);
    }
  }, [track]);

  const skillLookupEvent = useMemo(() => {
    if (level === 8 && currentEvent === "PH") {
      return "PL";
    }
    return currentEvent;
  }, [currentEvent, level]);

  const startValue = track === "gymact"
    ? calculateStartValueGymAct(selectedSkills, skillLookupEvent, bonuses, level, gymActStickApplied)
    : calculateStartValue(selectedSkills, skillLookupEvent, bonuses, level);

  const availableSkills = skillsData[skillLookupEvent] || [];

  const handleAddSkillFromSearch = (skill) => {
    if (currentEvent === "VT") {
      setSelectedSkills([{ ...skill, active: true }]);
    } else {
      if (selectedSkills.length < 10) {
        const skillWithInstanceId = { ...skill, instanceId: Date.now() + Math.random(), active: true };
        setSelectedSkills([...selectedSkills, skillWithInstanceId]);
      } else {
        alert("Maximum 10 skills per routine reached.");
      }
    }
    setSearchQuery("");
  };

  const handleAddSkill = () => {
    const skillName = newSkill.skillName;

    if (!skillName.trim()) {
      alert("Please select a skill");
      return;
    }

    const existingSkill = availableSkills.find(s => s.name === skillName);
    const skillToAdd = {
      name: skillName,
      code: existingSkill?.code || `SKILL-${Date.now()}`,
      value: newSkill.value,
      group: newSkill.group,
      apparatus: skillLookupEvent,
      active: true
    };

    if (editingSkillIndex !== null) {
      const updatedSkills = [...selectedSkills];
      const existingActive = updatedSkills[editingSkillIndex].active;
      updatedSkills[editingSkillIndex] = {
        ...skillToAdd,
        active: existingActive !== undefined ? existingActive : true
      };
      setSelectedSkills(updatedSkills);
      setEditingSkillIndex(null);
    } else {
      if (currentEvent === "VT") {
        setSelectedSkills([skillToAdd]);
      } else if (selectedSkills.length < 10) {
        setSelectedSkills([...selectedSkills, skillToAdd]);
      }
    }

    setNewSkill({ skillName: "", value: 0.3, group: "EG I" });
    setShowEditSkillDialog(false);
  };

  const handleEditSkill = (index) => {
    const skill = selectedSkills[index];
    setNewSkill({
      skillName: skill.name,
      value: skill.value,
      group: skill.group
    });
    setEditingSkillIndex(index);
    setShowEditSkillDialog(true);
  };

  const handleRemoveSkill = (index) => {
    const updatedSkills = [...selectedSkills];
    updatedSkills.splice(index, 1);
    setSelectedSkills(updatedSkills);
  };

  const handleMoveSkillUp = (index) => {
    if (index === 0) return;
    const updatedSkills = [...selectedSkills];
    const temp = updatedSkills[index];
    updatedSkills[index] = updatedSkills[index - 1];
    updatedSkills[index - 1] = temp;
    setSelectedSkills(updatedSkills);
  };

  const handleMoveSkillDown = (index) => {
    if (index === selectedSkills.length - 1) return;
    const updatedSkills = [...selectedSkills];
    const temp = updatedSkills[index];
    updatedSkills[index] = updatedSkills[index + 1];
    updatedSkills[index + 1] = temp;
    setSelectedSkills(updatedSkills);
  };

  const handleToggleBonus = (index) => {
    const updatedBonuses = [...bonuses];
    updatedBonuses[index] = {
      ...updatedBonuses[index],
      applied: !updatedBonuses[index].applied
    };
    setBonuses(updatedBonuses);
  };

  const handleToggleSkillActiveInRoutine = (index) => {
    const updatedSkills = [...selectedSkills];
    updatedSkills[index] = {
      ...updatedSkills[index],
      active: updatedSkills[index].active === false ? true : false
    };
    setSelectedSkills(updatedSkills);
  };

  const handleClearRoutine = () => {
    if (window.confirm("Are you sure you want to clear this routine?")) {
      setSelectedSkills([]);
      setBonuses(getBonusOptions(level).map(bonus => ({ ...bonus, applied: false })));
      setCurrentRoutineId(null);
      setRoutineName("");
      setSelectedAthleteId("");
    }
  };

  const handleSaveRoutine = async () => {
    if (!routineName.trim()) {
      alert("Please enter a routine name");
      return;
    }

    if (selectedSkills.length === 0) {
      alert("Please add at least one skill to the routine");
      return;
    }

    const athleteName = athletes.find(a => a.id === selectedAthleteId)?.name;

    const routineData = {
      track,
      gymActStickApplied,
      ownerId: currentUser.uid,
      name: routineName,
      athleteId: selectedAthleteId || null,
      athleteName: athleteName || null,
      event: currentEvent,
      level: level,
      skills: selectedSkills,
      bonuses: bonuses,
      startValue: startValue.total,
      updatedAt: new Date()
    };

    try {
      if (currentRoutineId) {
        const routineRef = doc(db, ROUTINES_COLLECTION, currentRoutineId);
        await updateDoc(routineRef, routineData);

        setSavedRoutines(prev =>
          prev.map(r =>
            r.id === currentRoutineId
              ? normalizeRoutine({ ...r, ...routineData }, currentEvent)
              : r
          )
        );
      } else {
        const docRef = await addDoc(collection(db, ROUTINES_COLLECTION), {
          ...routineData,
          createdAt: new Date()
        });

        const newRoutine = {
          id: docRef.id,
          ...routineData,
          createdAt: new Date()
        };

        setSavedRoutines(prev => [normalizeRoutine(newRoutine, currentEvent), ...prev]);
        setCurrentRoutineId(docRef.id);
      }

      setShowSaveDialog(false);
      alert(`Routine "${routineName}" saved successfully!`);
    } catch (error) {
      console.error("Error saving routine:", error);
      alert("Failed to save routine. Please try again.");
    }
  };

  const handleLoadRoutine = (routine) => {
    const normalizedRoutine = normalizeRoutine(routine, currentEvent);
    setIsLoadingRoutine(true);
    previousEventRef.current = normalizedRoutine.event;
    setCurrentEvent(normalizedRoutine.event);
    setLevel(normalizedRoutine.level);
    setTrack(normalizedRoutine.track || "national");
    setGymActStickApplied(normalizedRoutine.gymActStickApplied || false);
    setSelectedSkills(normalizedRoutine.skills);
    setBonuses(getBonusOptions(normalizedRoutine.level).map((bonus, i) => ({
      ...bonus,
      applied: normalizedRoutine.bonuses[i]?.applied ?? false
    })));
    setRoutineName(normalizedRoutine.name);
    setSelectedAthleteId(normalizedRoutine.athleteId || "");
    setCurrentRoutineId(normalizedRoutine.id);
    setShowLoadDialog(false);
    setTimeout(() => setIsLoadingRoutine(false), 100);
  };

  const handleCopyRoutine = (routine) => {
    const normalizedRoutine = normalizeRoutine(routine, currentEvent);
    setIsLoadingRoutine(true);
    previousEventRef.current = normalizedRoutine.event;
    setCurrentEvent(normalizedRoutine.event);
    setLevel(normalizedRoutine.level);
    setTrack(normalizedRoutine.track || "national");
    setGymActStickApplied(normalizedRoutine.gymActStickApplied || false);
    setSelectedSkills(normalizedRoutine.skills);
    setBonuses(getBonusOptions(normalizedRoutine.level).map((bonus, i) => ({
      ...bonus,
      applied: normalizedRoutine.bonuses[i]?.applied ?? false
    })));
    setRoutineName(`${normalizedRoutine.name} (Copy)`);
    setSelectedAthleteId(normalizedRoutine.athleteId || "");
    setCurrentRoutineId(null);
    setShowLoadDialog(false);
    setTimeout(() => setIsLoadingRoutine(false), 100);
  };

  const handleDeleteRoutine = async (routineId) => {
    if (!window.confirm("Are you sure you want to delete this routine?")) return;

    try {
      await deleteDoc(doc(db, ROUTINES_COLLECTION, routineId));
      setSavedRoutines(prev => prev.filter(r => r.id !== routineId));

      if (currentRoutineId === routineId) {
        setCurrentRoutineId(null);
        setRoutineName("");
      }
    } catch (error) {
      console.error("Error deleting routine:", error);
      alert("Failed to delete routine. Please try again.");
    }
  };

  if (!currentUser) {
    return null;
  }

  const isVault = currentEvent === "VT";
  const isGymAct = track === "gymact";
  const gymActStickDisabled = isVault || !["FX", "SR", "PB", "HB"].includes(currentEvent);
  const displayLevel = isGymAct ? "GymACT" : `L${level}`;

  const calcRoutine = (routine) => {
    const normalizedRoutine = normalizeRoutine(routine, currentEvent);

    if (normalizedRoutine.track === "gymact") {
      return calculateStartValueGymAct(
        normalizedRoutine.skills,
        normalizedRoutine.event,
        normalizedRoutine.bonuses,
        normalizedRoutine.level,
        normalizedRoutine.gymActStickApplied || false
      );
    }
    return calculateStartValue(
      normalizedRoutine.skills,
      normalizedRoutine.event,
      normalizedRoutine.bonuses,
      normalizedRoutine.level
    );
  };

  const levelLabelForRoutine = (routine) => {
    return routine.track === "gymact" ? "GymACT" : `L${routine.level}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ChartBarIcon className="w-6 h-6 text-blue-600" />
              Routine Start Value Calculator
              <span className="text-xs md:text-sm font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-2 py-1 rounded">
                {isGymAct ? "GymACT" : "National Track"}
              </span>
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1">
              Calculate D-scores for men's artistic gymnastics routines
            </p>
            {currentRoutineId && routineName && (
              <p className="text-sm text-green-600 mt-1">Currently editing: {routineName}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowLoadDialog(true)}
              className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <FolderOpenIcon className="w-4 h-4" />
              Load
            </button>
            <button
              onClick={() => setShowSaveDialog(true)}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              disabled={selectedSkills.length === 0}
            >
              <BookmarkIcon className="w-4 h-4" />
              Save
            </button>
            <Link
              to="/admin"
              className="w-full sm:w-auto px-4 py-2 text-center text-blue-600 hover:underline"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6 mb-4 md:mb-8">
          <div className="mb-4 md:mb-6">
            <h2 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-900 dark:text-white">
              Track
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTrack("national")}
                className={`px-4 py-2 rounded-md transition-colors font-medium ${
                  track === "national"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                }`}
              >
                National Track
              </button>
              <button
                onClick={() => setTrack("gymact")}
                className={`px-4 py-2 rounded-md transition-colors font-medium ${
                  track === "gymact"
                    ? "bg-emerald-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                }`}
              >
                GymACT
              </button>
            </div>
          </div>
          {!isGymAct && (
            <div className="mb-4 md:mb-6">
              <h2 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-900 dark:text-white">
                Select Level
              </h2>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                {[7, 8, 9, 10].map((lvl) => (
                  <button
                    key={lvl}
                    onClick={() => setLevel(lvl)}
                    className={`px-4 py-2 rounded-md transition-colors font-medium ${
                      level === lvl
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                  >
                    Level {lvl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isGymAct && (
            <div className="mb-4 md:mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h3 className="text-base md:text-lg font-semibold text-emerald-800 mb-2">GymACT Rules Summary</h3>
              <ul className="text-sm text-emerald-900 list-disc pl-5 space-y-1">
                <li>Short routine deductions: 5 skills -1.0, 4 skills -2.0, 3 skills -3.0, 2 skills -4.0, 1 skill -5.0.</li>
                <li>EG I: A or higher = 0.5.</li>
                <li>EG II/III (and EG IV for FX): A/B = 0.3, C = 0.4, D+ = 0.5.</li>
                <li>EG IV (except FX): value of the dismount.</li>
                <li>Stick bonus applies to FX, SR, PB, HB.</li>
              </ul>
              <div className="mt-3 flex items-center gap-3">
                <input
                  id="gymact-stick"
                  type="checkbox"
                  checked={gymActStickApplied}
                  onChange={() => setGymActStickApplied(prev => !prev)}
                  disabled={gymActStickDisabled}
                  className="h-4 w-4 text-emerald-600"
                />
                <label htmlFor="gymact-stick" className={`text-sm ${gymActStickDisabled ? "text-gray-400" : "text-emerald-900"}`}>
                  Apply GymACT stick bonus (FX/SR/PB/HB)
                </label>
              </div>
              {gymActStickDisabled && (
                <p className="text-xs text-gray-500 mt-1">Stick bonus not available for this event.</p>
              )}
            </div>
          )}

          <div className="mb-4 md:mb-6">
            <h2 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-900 dark:text-white">
              Select Event
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {EVENTS.map((event) => (
                <button
                  key={event.id}
                  onClick={() => setCurrentEvent(event.id)}
                  className={`p-2 md:p-3 rounded-lg text-center transition-colors ${
                    currentEvent === event.id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  <div className="text-sm md:text-base font-medium">{event.shortName}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2">
              <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">
                    Add Skills
                    <span className="text-xs md:text-sm font-normal text-gray-500 ml-2">
                      ({availableSkills.length} available)
                    </span>
                  </h2>
                </div>

                <div className="relative mb-3">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search skills by name or code..."
                    className="w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[
                    "All",
                    ...ELEMENT_GROUPS.filter(eg => availableSkills.some(s => s.group === eg))
                  ].map(filter => (
                    <button
                      key={filter}
                      onClick={() => setSelectedEGFilter(filter)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        selectedEGFilter === filter
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {filter}
                      <span className="ml-1 opacity-75">
                        ({filter === "All"
                          ? availableSkills.filter(s => !searchQuery.trim() || s.name.toLowerCase().includes(searchQuery.toLowerCase()) || (s.code && s.code.toLowerCase().includes(searchQuery.toLowerCase()))).length
                          : availableSkills.filter(s => s.group === filter && (!searchQuery.trim() || s.name.toLowerCase().includes(searchQuery.toLowerCase()) || (s.code && s.code.toLowerCase().includes(searchQuery.toLowerCase())))).length
                        })
                      </span>
                    </button>
                  ))}
                </div>

                <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg bg-white">
                  {(() => {
                    const groupOrder = ["EG I", "EG II", "EG III", "EG IV", "EG V", "Junior A"];
                    let filtered = availableSkills;
                    if (searchQuery.trim()) {
                      const q = searchQuery.toLowerCase();
                      filtered = filtered.filter(s =>
                        s.name.toLowerCase().includes(q) || (s.code && s.code.toLowerCase().includes(q))
                      );
                    }
                    if (selectedEGFilter !== "All") {
                      filtered = filtered.filter(s => s.group === selectedEGFilter);
                    }

                    if (filtered.length === 0) {
                      return (
                        <div className="p-8 text-center text-gray-400">
                          <svg className="mx-auto h-10 w-10 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                          </svg>
                          <p className="text-sm font-medium">No skills found</p>
                          <p className="text-xs mt-1">Try adjusting your search or filter</p>
                        </div>
                      );
                    }

                    const groupMap = {};
                    filtered.forEach(skill => {
                      if (!groupMap[skill.group]) groupMap[skill.group] = [];
                      groupMap[skill.group].push(skill);
                    });

                    const orderedGroups = groupOrder.filter(g => groupMap[g]);
                    Object.keys(groupMap).forEach(g => {
                      if (!orderedGroups.includes(g)) orderedGroups.push(g);
                    });

                    return orderedGroups.map(group => (
                      <div key={group}>
                        <div className="sticky top-0 z-10 bg-gradient-to-r from-gray-100 to-gray-50 px-3 py-2 border-b border-gray-200">
                          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                            {group}
                            <span className="ml-2 text-gray-400 font-normal normal-case">
                              ({groupMap[group].length} skill{groupMap[group].length !== 1 ? "s" : ""})
                            </span>
                          </h4>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {groupMap[group].map(skill => {
                            const canAdd = isVault || selectedSkills.length < 10;
                            return (
                              <button
                                key={skill.code}
                                onClick={() => {
                                  if (canAdd) handleAddSkillFromSearch(skill);
                                }}
                                disabled={!canAdd}
                                className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-all group ${
                                  canAdd
                                    ? "hover:bg-blue-50 cursor-pointer"
                                    : "opacity-50 cursor-not-allowed"
                                }`}
                              >
                                <span className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                                  skill.value >= 0.5
                                    ? "bg-red-100 text-red-700"
                                    : skill.value >= 0.3
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-green-100 text-green-700"
                                }`}>
                                  {skill.value.toFixed(1)}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {skill.name}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {skill.code}
                                  </div>
                                </div>
                                {canAdd ? (
                                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <PlusIcon className="w-4 h-4" />
                                  </span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                <div>
                  <h3 className="text-sm md:text-base font-semibold mb-2">
                    Selected Skills ({selectedSkills.length}/{isVault ? 1 : 10})
                  </h3>
                  {selectedSkills.length === 0 ? (
                    <p className="text-xs md:text-sm text-gray-500 italic">No skills selected yet. Browse or search skills above to add them to your routine.</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedSkills.map((skill, index) => (
                        <div
                          key={index}
                          className={`flex flex-col sm:flex-row sm:justify-between sm:items-center p-2 md:p-3 bg-white rounded border gap-2 ${
                            skill.active === false ? "opacity-50" : ""
                          }`}
                        >
                          <div className="flex items-center gap-1 md:gap-2 flex-1 min-w-0">
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => handleMoveSkillUp(index)}
                                disabled={index === 0}
                                className={`p-0.5 md:p-1 rounded ${
                                  index === 0
                                    ? "text-gray-300 cursor-not-allowed"
                                    : "text-gray-600 hover:bg-gray-200"
                                }`}
                                title="Move up"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3 md:w-4 md:h-4">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleMoveSkillDown(index)}
                                disabled={index === selectedSkills.length - 1}
                                className={`p-0.5 md:p-1 rounded ${
                                  index === selectedSkills.length - 1
                                    ? "text-gray-300 cursor-not-allowed"
                                    : "text-gray-600 hover:bg-gray-200"
                                }`}
                                title="Move down"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3 md:w-4 md:h-4">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                </svg>
                              </button>
                            </div>
                            <button
                              onClick={() => handleToggleSkillActiveInRoutine(index)}
                              className={`p-1 md:p-2 rounded flex-shrink-0 ${
                                skill.active === false
                                  ? "text-gray-400 hover:bg-gray-100"
                                  : "text-green-600 hover:bg-green-50"
                              }`}
                              title={skill.active === false ? "Activate skill (include in scoring)" : "Deactivate skill (exclude from scoring)"}
                            >
                              {skill.active === false ? (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs md:text-sm font-medium truncate">{skill.name}</div>
                              <div className="text-xs text-gray-600 truncate">{skill.code} | {skill.group}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 md:gap-2 justify-end sm:justify-start">
                            <span className="text-xs md:text-sm font-semibold bg-blue-100 text-blue-800 px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                              {skill.value.toFixed(1)}
                            </span>
                            <button
                              onClick={() => handleEditSkill(index)}
                              className="p-1 md:p-2 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                              title="Edit"
                            >
                              <PencilIcon className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveSkill(index)}
                              className="p-1 md:p-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                              title="Remove"
                            >
                              <XMarkIcon className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedSkills.length > 0 && (
                    <button
                      onClick={handleClearRoutine}
                      className="mt-3 md:mt-4 w-full sm:w-auto px-3 md:px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm md:text-base"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-3 md:p-4 rounded-lg mt-3 md:mt-4">
                <h2 className="text-base md:text-lg font-semibold mb-2 md:mb-3">Bonus Points</h2>
                <div className="space-y-2">
                  {bonuses.map((bonus, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white border rounded">
                      <div className="flex items-center min-w-0 flex-1">
                        <input
                          type="checkbox"
                          id={`bonus-${index}`}
                          checked={bonus.applied}
                          onChange={() => handleToggleBonus(index)}
                          className="mr-2 md:mr-3 h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0"
                        />
                        <label htmlFor={`bonus-${index}`} className="text-xs md:text-sm text-gray-700 truncate">{bonus.name}</label>
                      </div>
                      <span className="text-xs md:text-sm font-semibold bg-green-100 text-green-800 px-1.5 md:px-2 py-0.5 md:py-1 rounded ml-2 flex-shrink-0">
                        +{bonus.value.toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-4">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-4 md:p-6 rounded-lg shadow-lg">
                <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Start Value</h2>
                <div className="text-4xl md:text-5xl font-bold mb-4 md:mb-6">{startValue.total.toFixed(1)}</div>

                <div className="space-y-2 md:space-y-3 text-xs md:text-sm">
                  <div className="flex justify-between items-center pb-2 border-b border-blue-400">
                    <span>Base D-Score:</span>
                    <span className="font-semibold">{startValue.breakdown.baseDScore.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-blue-400">
                    <span>Composition:</span>
                    <span className="font-semibold">
                      {currentEvent === "VT" ? "0.0" : startValue.breakdown.composition.toFixed(1)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-blue-400">
                    <span>Connection Value:</span>
                    <span className="font-semibold">{startValue.breakdown.connectionValue.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-blue-400">
                    <span>Bonus:</span>
                    <span className="font-semibold">{startValue.breakdown.bonus.toFixed(1)}</span>
                  </div>
                  {isGymAct && (
                    <div className="flex justify-between items-center pb-2 border-b border-blue-400">
                      <span>Stick Bonus:</span>
                      <span className="font-semibold">{(startValue.breakdown.stickBonus || 0).toFixed(1)}</span>
                    </div>
                  )}
                  {startValue.breakdown.egDeduction !== 0 && (
                    <div className="flex justify-between items-center pb-2 border-b border-red-400">
                      <span>EG Deduction:</span>
                      <span className="font-semibold text-red-300">{startValue.breakdown.egDeduction.toFixed(1)}</span>
                    </div>
                  )}
                </div>

                {currentEvent !== "VT" && (
                  <div className="mt-4 md:mt-6">
                    <h3 className="text-sm md:text-base font-semibold mb-2">Element Group Coverage</h3>
                    {level === 7 ? (
                      <>
                        <div className="text-xs text-blue-200 mb-1">
                          Any <span className="font-semibold">3 of 4</span> EGs required for full credit
                        </div>
                        <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                          {(() => {
                            const egEntries = Object.entries(startValue.egCoverage)
                              .filter(([eg]) => ["EG I", "EG II", "EG III", "EG IV"].includes(eg));
                            const coveredCount = egEntries.filter(([, covered]) => covered).length;

                            return egEntries.map(([eg, covered]) => {
                              const shouldShowYellow = !covered && coveredCount >= 3;

                              return (
                                <div
                                  key={eg}
                                  className={`px-2 py-1 rounded text-center text-xs ${
                                    covered
                                      ? "bg-green-500 text-white"
                                      : shouldShowYellow
                                        ? "bg-yellow-500 text-white"
                                        : "bg-red-400 text-white"
                                  }`}
                                >
                                  {eg}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </>
                    ) : (
                      <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                        {Object.entries(startValue.egCoverage).map(([eg, covered]) => (
                          <div
                            key={eg}
                            className={`px-2 py-1 rounded text-center text-xs ${
                              covered ? "bg-green-500 text-white" : "bg-red-400 text-white"
                            }`}
                          >
                            {eg}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-green-600 to-green-700 text-white p-6 rounded-lg shadow-lg mt-4">
                <h2 className="text-lg font-bold mb-2">Perfect Execution Score</h2>
                <div className="text-4xl font-bold mb-3">{(startValue.total + 10.0).toFixed(1)}</div>
                <div className="text-sm opacity-90">
                  <div className="flex justify-between items-center pb-2 border-b border-green-400">
                    <span>D-Score (Start Value):</span>
                    <span className="font-semibold">{startValue.total.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span>E-Score (Perfect):</span>
                    <span className="font-semibold">10.0</span>
                  </div>
                </div>
                <p className="text-xs mt-3 opacity-75 italic">
                  This assumes no execution deductions
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow mt-4">
                <h2 className="text-lg font-semibold mb-3">Routine Info</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Event:</span>
                    <span className="font-medium">{EVENTS.find(e => e.id === currentEvent)?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Level:</span>
                    <span className="font-medium">{displayLevel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Skills:</span>
                    <span className="font-medium">{selectedSkills.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Missing EGs:</span>
                    <span className={`font-medium ${startValue.missingEGs > 0 ? "text-red-600" : "text-green-600"}`}>
                      {startValue.missingEGs}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {savedRoutines.length > 0 && (
          <div className="mt-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
                <div className="text-sm font-medium opacity-90">Total Routines</div>
                <div className="text-3xl font-bold mt-2">{savedRoutines.length}</div>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
                <div className="text-sm font-medium opacity-90">Avg Start Value</div>
                <div className="text-3xl font-bold mt-2">
                  {(savedRoutines.reduce((sum, r) => sum + (r.startValue || 0), 0) / savedRoutines.length).toFixed(2)}
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
                <div className="text-sm font-medium opacity-90">Highest SV</div>
                <div className="text-3xl font-bold mt-2">
                  {Math.max(...savedRoutines.map(r => r.startValue || 0)).toFixed(2)}
                </div>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-lg shadow-lg">
                <div className="text-sm font-medium opacity-90">Active Athletes</div>
                <div className="text-3xl font-bold mt-2">
                  {new Set(savedRoutines.map(r => r.athleteId).filter(Boolean)).size}
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <ChartBarIcon className="w-6 h-6 text-blue-600" />
                Breakdown by Event & Level
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Event</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Level</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Routines</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Avg SV</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Range</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const groups = {};
                      savedRoutines.forEach(r => {
                        const trackKey = r.track || "national";
                        const key = `${r.event}-${trackKey}-${r.level}`;
                        if (!groups[key]) {
                          groups[key] = { count: 0, sum: 0, min: Infinity, max: -Infinity };
                        }
                        groups[key].count += 1;
                        groups[key].sum += r.startValue || 0;
                        groups[key].min = Math.min(groups[key].min, r.startValue || 0);
                        groups[key].max = Math.max(groups[key].max, r.startValue || 0);
                      });
                      return Object.entries(groups)
                        .sort((a, b) => {
                          const [eventA, trackA, levelA] = a[0].split("-");
                          const [eventB, trackB, levelB] = b[0].split("-");
                          if (eventA !== eventB) return eventA.localeCompare(eventB);
                          if (trackA !== trackB) return trackA.localeCompare(trackB);
                          return Number(levelB) - Number(levelA);
                        })
                        .map(([key, stats]) => {
                          const [event, trackKey, lvl] = key.split("-");
                          const eventName = EVENTS.find(e => e.id === event)?.shortName || event;
                          const levelLabel = trackKey === "gymact" ? "GymACT" : `L${lvl}`;
                          return (
                            <tr key={key} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3">
                                <span className="font-medium text-gray-900">{eventName}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                  {levelLabel}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-block px-3 py-1 bg-gray-100 rounded-full text-gray-700 font-medium">
                                  {stats.count}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-900">
                                {(stats.sum / stats.count).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-600">
                                {stats.min.toFixed(2)} - {stats.max.toFixed(2)}
                              </td>
                            </tr>
                          );
                        });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">Element Group Completion</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {(() => {
                  const egCounts = {};
                  savedRoutines.forEach(r => {
                    const calc = calcRoutine(r);
                    const missing = typeof calc.missingEGs === "number" ? calc.missingEGs : 0;
                    egCounts[missing] = (egCounts[missing] || 0) + 1;
                  });
                  return [0, 1, 2, 3, 4].map(missing => {
                    const count = egCounts[missing] || 0;
                    const percentage = ((count / savedRoutines.length) * 100).toFixed(0);
                    const bgColor = missing === 0 ? "bg-green-50 border-green-500" :
                      missing === 1 ? "bg-yellow-50 border-yellow-500" :
                        "bg-red-50 border-red-500";
                    const textColor = missing === 0 ? "text-green-700" :
                      missing === 1 ? "text-yellow-700" :
                        "text-red-700";
                    return (
                      <div key={missing} className={`border-l-4 ${bgColor} p-4 rounded`}>
                        <div className={`text-2xl font-bold ${textColor}`}>{count}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {missing} missing EG{missing !== 1 ? "s" : ""}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{percentage}%</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-4 md:p-6">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Missing Element Groups by Event</h3>
              <div className="overflow-x-auto -mx-4 md:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden">
                    <table className="min-w-full text-xs md:text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700">Event</th>
                          <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 hidden sm:table-cell">Athlete</th>
                          <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700">Routine</th>
                          <th className="px-2 md:px-4 py-2 md:py-3 text-center font-semibold text-gray-700">Level</th>
                          <th className="px-2 md:px-4 py-2 md:py-3 text-center font-semibold text-gray-700">Missing</th>
                          <th className="px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-gray-700 hidden md:table-cell">Missing Groups</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(() => {
                          const routinesWithMissingEGs = savedRoutines
                            .filter(routine => routine.event !== "VT")
                            .map(routine => {
                  const calc = calcRoutine(routine);
                              const missingCount = typeof calc.missingEGs === "number" ? calc.missingEGs : 0;
                              const missingEGsList = Object.entries(calc.egCoverage)
                                .filter(([eg, covered]) => !covered && ["EG I", "EG II", "EG III", "EG IV"].includes(eg))
                                .map(([eg]) => eg);
                              return {
                                ...routine,
                                missingCount,
                                missingEGsList
                              };
                            })
                            .filter(r => r.missingCount > 0)
                            .sort((a, b) => {
                              if (a.event !== b.event) return a.event.localeCompare(b.event);
                              if (a.missingCount !== b.missingCount) return b.missingCount - a.missingCount;
                              return (a.athleteName || "General").localeCompare(b.athleteName || "General");
                            });

                          if (routinesWithMissingEGs.length === 0) {
                            return (
                              <tr>
                                <td colSpan={6} className="px-2 md:px-4 py-6 md:py-8 text-center text-gray-500 text-xs md:text-sm">
                                  All routines have complete Element Group coverage!
                                </td>
                              </tr>
                            );
                          }

                          return routinesWithMissingEGs.map(routine => (
                            <tr key={routine.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-2 md:px-4 py-2 md:py-3">
                                <span className="font-medium text-gray-900">
                                  {EVENTS.find(e => e.id === routine.event)?.shortName || routine.event}
                                </span>
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 text-gray-700 hidden sm:table-cell">
                                {routine.athleteName || "General"}
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 text-gray-700">
                                <div className="truncate max-w-[120px] md:max-w-none">{routine.name}</div>
                                <div className="text-xs text-gray-500 sm:hidden mt-0.5">{routine.athleteName || "General"}</div>
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 text-center">
                                <span className="inline-block px-1.5 md:px-2 py-0.5 md:py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                  {levelLabelForRoutine(routine)}
                                </span>
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 text-center">
                                <span className={`inline-block px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-semibold ${
                                  routine.missingCount === 1 ? "bg-yellow-100 text-yellow-800" :
                                    routine.missingCount === 2 ? "bg-orange-100 text-orange-800" :
                                      "bg-red-100 text-red-800"
                                }`}>
                                  {routine.missingCount}
                                </span>
                                <div className="md:hidden mt-1 flex gap-1 justify-center flex-wrap">
                                  {routine.missingEGsList.map(eg => (
                                    <span key={eg} className="inline-block px-1.5 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                                      {eg.replace("EG ", "")}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-2 md:px-4 py-2 md:py-3 hidden md:table-cell">
                                <div className="flex gap-1 flex-wrap">
                                  {routine.missingEGsList.map(eg => (
                                    <span key={eg} className="inline-block px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                                      {eg}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <ChartBarIcon className="w-6 h-6 text-orange-500" />
                All-Around Scores by Athlete
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Athlete</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Level</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Floor</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Pommel</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Rings</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Vault</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">P-Bars</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">High Bar</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-blue-50">All-Around<br /><span className="text-xs font-normal">(D-Score)</span></th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Perfect Score<br /><span className="text-xs font-normal">(D+E)</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const athleteScores = {};

                      savedRoutines.forEach(routine => {
                        if (!routine.athleteId) return;

                        if (!athleteScores[routine.athleteId]) {
                          athleteScores[routine.athleteId] = {
                            name: routine.athleteName || "Unknown",
                            level: routine.track === "gymact" ? "GymACT" : `L${routine.level}`,
                            scores: {},
                            total: 0,
                            eventsCount: 0
                          };
                        }

                        const currentScore = athleteScores[routine.athleteId].scores[routine.event];
                        if (!currentScore || routine.startValue > currentScore) {
                          athleteScores[routine.athleteId].scores[routine.event] = routine.startValue;
                        }
                      });

                      Object.values(athleteScores).forEach(athlete => {
                        athlete.total = Object.values(athlete.scores).reduce((sum, score) => sum + (score || 0), 0);
                        athlete.eventsCount = Object.keys(athlete.scores).length;
                      });

                      const athleteList = Object.entries(athleteScores)
                        .map(([id, data]) => ({ id, ...data }))
                        .sort((a, b) => b.total - a.total);

                      if (athleteList.length === 0) {
                        return (
                          <tr>
                            <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                              No athlete routines found. Assign routines to athletes to see all-around scores.
                            </td>
                          </tr>
                        );
                      }

                      return athleteList.map(athlete => {
                        const perfectScore = athlete.total + (athlete.eventsCount * 10.0);

                        return (
                          <tr key={athlete.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-900">{athlete.name}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                {athlete.level}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={athlete.scores["FX"] ? "font-semibold text-gray-900" : "text-gray-400"}>
                                {athlete.scores["FX"]?.toFixed(2) ?? "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={athlete.scores["PH"] ? "font-semibold text-gray-900" : "text-gray-400"}>
                                {athlete.scores["PH"]?.toFixed(2) ?? "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={athlete.scores["SR"] ? "font-semibold text-gray-900" : "text-gray-400"}>
                                {athlete.scores["SR"]?.toFixed(2) ?? "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={athlete.scores["VT"] ? "font-semibold text-gray-900" : "text-gray-400"}>
                                {athlete.scores["VT"]?.toFixed(2) ?? "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={athlete.scores["PB"] ? "font-semibold text-gray-900" : "text-gray-400"}>
                                {athlete.scores["PB"]?.toFixed(2) ?? "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={athlete.scores["HB"] ? "font-semibold text-gray-900" : "text-gray-400"}>
                                {athlete.scores["HB"]?.toFixed(2) ?? "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right bg-blue-50">
                              <div className="flex flex-col items-end">
                                <span className="font-bold text-lg text-blue-900">{athlete.total.toFixed(2)}</span>
                                <span className="text-xs text-gray-600">({athlete.eventsCount}/6 events)</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right bg-green-50">
                              <div className="flex flex-col items-end">
                                <span className="font-bold text-lg text-green-900">{perfectScore.toFixed(2)}</span>
                                <span className="text-xs text-gray-600">w/ perfect E</span>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-4 italic">
                All-Around D-Score is the sum of the highest start value for each event. Perfect Score adds 10.0 execution points per event. Only routines assigned to specific athletes are included.
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">Recent Routines</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Routine</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Athlete</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Event</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Level</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Start Value</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Skills</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {savedRoutines.slice(0, 10).map(routine => (
                      <tr key={routine.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{routine.name}</td>
                        <td className="px-4 py-3 text-gray-700">{routine.athleteName || "General"}</td>
                        <td className="px-4 py-3">
                          <span className="text-gray-700">{EVENTS.find(e => e.id === routine.event)?.shortName || routine.event}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            {levelLabelForRoutine(routine)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-gray-900">{routine.startValue?.toFixed(2) ?? "-"}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-block px-3 py-1 bg-gray-100 rounded-full text-gray-700">
                            {routine.skills?.length ?? "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600">
                          {routine.updatedAt?.toLocaleDateString?.() ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {savedRoutines.length > 10 && (
                <p className="text-sm text-gray-500 text-center mt-4">
                  Showing 10 most recent routines of {savedRoutines.length} total
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-md">
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Save Routine</h3>
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1">Routine Name *</label>
                <input
                  type="text"
                  value={routineName}
                  onChange={(e) => setRoutineName(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                  placeholder="e.g., Floor Competition Routine"
                />
              </div>
              <div>
                <label className="block text-xs md:text-sm font-medium mb-1">Athlete (Optional)</label>
                <select
                  value={selectedAthleteId}
                  onChange={(e) => setSelectedAthleteId(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">None - General routine</option>
                  {athletes.map(athlete => (
                    <option key={athlete.id} value={athlete.id}>
                      {athlete.name} ({athlete.level})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-6">
              <button
                onClick={handleSaveRoutine}
                className="flex-1 px-3 md:px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm md:text-base"
              >
                Save
              </button>
              <button
                onClick={() => setShowSaveDialog(false)}
                className="flex-1 px-3 md:px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm md:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoadDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-2xl max-h-[85vh] md:max-h-[80vh] overflow-y-auto">
            <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Load Routine</h3>

            <div className="mb-3 md:mb-4">
              <label className="block text-xs md:text-sm font-medium mb-1">Filter by Athlete</label>
              <select
                value={routineFilterAthleteId}
                onChange={(e) => setRoutineFilterAthleteId(e.target.value)}
                className="w-full p-2 border rounded text-sm"
              >
                <option value="">All Routines</option>
                <option value="general">General (No Athlete)</option>
                {athletes.map(athlete => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.name} (Level {athlete.level})
                  </option>
                ))}
              </select>
            </div>

            {isLoadingRoutines ? (
              <p className="text-gray-500 text-center py-8">Loading routines...</p>
            ) : savedRoutines.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No saved routines yet.</p>
            ) : (() => {
              const filteredRoutines = savedRoutines.filter(routine => {
                if (!routineFilterAthleteId) return true;
                if (routineFilterAthleteId === "general") return !routine.athleteId;
                return routine.athleteId === routineFilterAthleteId;
              });

              if (filteredRoutines.length === 0) {
                return <p className="text-gray-500 text-center py-8">No routines found for the selected filter.</p>;
              }

              return (
                <div className="space-y-2">
                  {filteredRoutines.map(routine => (
                    <div
                      key={routine.id}
                      className="flex justify-between items-center p-4 border rounded hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{routine.name}</div>
                        <div className="text-xs text-gray-600">
                        {EVENTS.find(e => e.id === routine.event)?.name} | {levelLabelForRoutine(routine)} |
                          {routine.athleteName ? ` ${routine.athleteName}` : " General"} |
                          Start Value: {routine.startValue.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Updated: {routine.updatedAt?.toLocaleDateString?.() ?? "-"}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleLoadRoutine(routine)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => handleCopyRoutine(routine)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          title="Copy this routine to create a new one"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => handleDeleteRoutine(routine.id)}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowLoadDialog(false);
                  setRoutineFilterAthleteId("");
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditSkillDialog && editingSkillIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 md:p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <PencilIcon className="w-5 h-5 text-blue-600" />
              Edit Skill
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skill Name</label>
                <input
                  type="text"
                  value={newSkill.skillName}
                  readOnly
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty Value</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={newSkill.value}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, value: parseFloat(e.target.value) }))}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Element Group</label>
                <select
                  value={newSkill.group}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, group: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {ELEMENT_GROUPS.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddSkill}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              >
                Update Skill
              </button>
              <button
                onClick={() => {
                  setShowEditSkillDialog(false);
                  setEditingSkillIndex(null);
                  setNewSkill({ skillName: "", value: 0.3, group: "EG I" });
                }}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutineCalculator;
